import { useEffect, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { FetchRequest } from '@rails/request.js'

const DEBOUNCE_MS = 250

function useBoardSearch(boardId, query) {
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const trimmed = (debouncedQuery || '').trim()
  const enabled = !!boardId && trimmed.length > 0
  const isActive = !!query && query.trim().length > 0

  const result = useQuery({
    queryKey: ['kanbanBoardSearch', boardId, trimmed],
    queryFn: async () => {
      const url = `/kanban_boards/${boardId}/search_cards?q=${encodeURIComponent(trimmed)}`
      const response = await new FetchRequest('get', url, {
        headers: { Accept: 'application/json' }
      }).perform()
      return response.json
    },
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData
  })

  const perColumnCounts = useMemo(() => {
    const raw = result.data?.per_column_counts || {}
    const out = {}
    for (const [columnId, count] of Object.entries(raw)) {
      out[Number(columnId)] = count
    }
    return out
  }, [result.data])

  return {
    isActive,
    isPending: isActive && (result.isFetching || trimmed !== (query || '').trim()),
    debouncedQuery: trimmed,
    totalCount: result.data?.total_count || 0,
    perColumnCounts
  }
}

export default useBoardSearch
